/**
 * Fonctions utilitaires pures pour manipuler la structure de données MindMap
 * Pas de React - juste du JavaScript
 */

/**
 * Crée le node racine initial de la mindmap
 * @param {string} content - Le contenu/titre du node racine
 * @param {string} type - Le type du node ('text' ou 'button') - défaut: 'text'
 * @returns {Object} Le node racine avec structure complète
 */
export const createRootNode = (content, type = 'text') => {
	return {
		id: 'root',
		type,
		content,
		isExpanded: true,
		children: [],
	};
};

/**
 * Ajoute un node à un endroit précis dans l'arbre
 * @param {Object} data - La structure mindmap entière (copie immutable)
 * @param {Array<number>} nodePath - Chemin vers le parent [0, 2, 1] signifie children[0].children[2].children[1]
 *                                   [] signifie ajouter au root
 * @param {Object} newNode - Le nouveau node à ajouter { id, type, content, isExpanded?, children?, buttonAction? }
 * @returns {Object} Nouvelle structure mindmap avec le node ajouté
 */
export const addNodeAtPath = (data, nodePath, newNode) => {
	if (!data) return data;

	// Si le chemin est vide, on ajoute au root (cas rare)
	if (!nodePath || nodePath.length === 0) {
		return {
			...data,
			children: [...(data.children || []), newNode],
		};
	}

	// Sinon, on navigue jusqu'au parent et on ajoute l'enfant
	const navigate = (node, path, index = 0) => {
		if (index === path.length) {
			// On est arrivé au parent, ajouter le nouvel enfant
			return {
				...node,
				children: [...(node.children || []), newNode],
			};
		}

		const childIndex = path[index];
		const children = node.children || [];

		if (childIndex < 0 || childIndex > children.length) {
			console.warn(`Invalid path index ${childIndex} at depth ${index}`);
			return node;
		}

		// Récursivement mettre à jour l'enfant
		return {
			...node,
			children: children.map((child, idx) =>
				idx === childIndex ? navigate(child, path, index + 1) : child
			),
		};
	};

	return navigate(data, nodePath);
};

/**
 * Supprime un node à un endroit précis dans l'arbre
 * @param {Object} data - La structure mindmap entière (copie immutable)
 * @param {Array<number>} nodePath - Chemin vers le node à supprimer [0, 2, 1]
 *                                   le dernier index pointe vers le node lui-même
 * @returns {Object} Nouvelle structure mindmap avec le node supprimé
 */
export const removeNodeAtPath = (data, nodePath) => {
	if (!data || !nodePath || nodePath.length === 0) {
		return data;
	}

	// Cas spécial: on ne peut pas supprimer la root
	if (nodePath.length === 1) {
		const parentIndex = nodePath[0];
		return {
			...data,
			children: (data.children || []).filter((_, idx) => idx !== parentIndex),
		};
	}

	// Naviguer jusqu'au parent du node à supprimer
	const parentPath = nodePath.slice(0, -1);
	const nodeIndexToRemove = nodePath[nodePath.length - 1];

	const navigate = (node, path, index = 0) => {
		if (index === path.length) {
			// On est arrivé au parent, supprimer l'enfant
			return {
				...node,
				children: (node.children || []).filter((_, idx) => idx !== nodeIndexToRemove),
			};
		}

		const childIndex = path[index];
		const children = node.children || [];

		if (childIndex < 0 || childIndex >= children.length) {
			console.warn(`Invalid path index ${childIndex} at depth ${index}`);
			return node;
		}

		// Récursivement mettre à jour l'enfant
		return {
			...node,
			children: children.map((child, idx) =>
				idx === childIndex ? navigate(child, path, index + 1) : child
			),
		};
	};

	return navigate(data, parentPath);
};

/**
 * Utilitaire: trouver le chemin d'un node par son ID
 * @param {Object} data - La structure mindmap
 * @param {string} targetId - L'ID du node à chercher
 * @returns {Array<number>|null} Le chemin vers le node ou null si non trouvé
 */
export const findNodePathById = (data, targetId) => {
	const search = (node, currentPath = []) => {
		if (node.id === targetId) {
			return currentPath;
		}

		if (node.children && node.children.length > 0) {
			for (let i = 0; i < node.children.length; i++) {
				const result = search(node.children[i], [...currentPath, i]);
				if (result !== null) {
					return result;
				}
			}
		}

		return null;
	};

	return search(data);
};

/**
 * Ajouter un node enfant à un parent identifié par son ID (wrapper pratique)
 * @param {Object} data - La structure mindmap
 * @param {string} parentId - L'ID du parent
 * @param {Object} newNode - Le nouveau node à ajouter
 * @returns {Object} Nouvelle structure ou l'originale si parent pas trouvé
 */
export const addNodeToParentById = (data, parentId, newNode) => {
	const parentPath = findNodePathById(data, parentId);
	if (parentPath === null) {
		console.warn(`Parent with ID "${parentId}" not found`);
		return data;
	}
	return addNodeAtPath(data, parentPath, newNode);
};

/**
 * Supprimer un node par son ID
 * @param {Object} data - La structure mindmap
 * @param {string} nodeId - L'ID du node à supprimer
 * @returns {Object} Nouvelle structure ou l'originale si node pas trouvé
 */
export const removeNodeById = (data, nodeId) => {
	if (nodeId === 'root') {
		console.warn('Cannot remove root node');
		return data;
	}

	const nodePath = findNodePathById(data, nodeId);
	if (nodePath === null) {
		console.warn(`Node with ID "${nodeId}" not found`);
		return data;
	}

	return removeNodeAtPath(data, nodePath);
};

/**
 * EXEMPLES D'USAGE
 * 
 * 1. Créer la root:
 *    const mindmap = createRootNode('Apprentissage VARK');
 * 
 * 2. Ajouter un node enfant avec chemin:
 *    const newNode = { id: 'visual1', type: 'text', content: 'Mon contenu', isExpanded: true, children: [] };
 *    const updated = addNodeAtPath(mindmap, [0], newNode);  // Ajoute à children[0]
 * 
 * 3. Ajouter un node enfant par ID (plus facile):
 *    const newChild = { id: 'visual-sub', type: 'text', content: 'Sous-branche', isExpanded: false, children: [] };
 *    const updated = addNodeToParentById(mindmap, 'visual', newChild);
 * 
 * 4. Supprimer un node par ID:
 *    const updated = removeNodeById(mindmap, 'visual-sub');
 * 
 * 5. Supprimer un node par chemin:
 *    const updated = removeNodeAtPath(mindmap, [0, 1, 2]);  // Supprime children[0].children[1].children[2]
 * 
 * 6. Trouver le chemin d'un node:
 *    const path = findNodePathById(mindmap, 'visual');  // Retourne [0] si visual est le 1er enfant du root
 */
